import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation } from 'react-router-dom';
import HomePage from './components/HomePage';
import NotFoundPage from './components/NotFoundPage';
import BlogPage from './components/BlogPage';
import AdminPanel from './components/AdminPanel';
import CoursesPage from './components/CoursesPage';
import MentorAuth from './components/MentorAuth';
import MentorDashboard from './components/MentorDashboard';
import Header from './components/Header';
import AboutUs from './components/AboutUs';
import TermsOfService from './components/TermsOfService';
import PrivacyPolicy from './components/PrivacyPolicy';
import FeaturesGPT from './components/FeaturesGPT';
import ScrollToTop from './components/ScrollToTop';
import GptClickTracker from './components/GptClickTracker';
import MetaLandingPage from './components/MetaLandingPageNew';
import ExitIntentPopup from './components/ExitIntentPopup';
import AskPage from './components/AskPage';
import ResultsPage from './components/ResultsPage';
import MarketTicker from './components/MarketTicker';
import DematPage from './components/DematPage';
import CallPage from './components/CallPage';
import IndexAnalysisPage, { NIFTY_CONFIG, BANK_NIFTY_CONFIG } from './components/IndexAnalysisPage';
import { performanceMonitor } from './utils/performance';
import { trackPageView } from './utils/analytics';

function AppContent() {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMentorLoggedIn, setIsMentorLoggedIn] = useState(false);
  const location = useLocation();

  const isMetaLandingPage = location.pathname === '/ad';

  // Derive active page for header nav highlights
  const currentPage = location.pathname.startsWith('/about') ? 'about'
    : location.pathname.startsWith('/features') ? 'features'
    : location.pathname.startsWith('/blog') ? 'blog'
    : 'home';

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Track every route change as a page_view in GTM / GA4
  useEffect(() => {
    trackPageView(location.pathname + location.search, document.title);
  }, [location.pathname, location.search]);

  useEffect(() => {
    return () => { performanceMonitor.destroy(); };
  }, []);

  if (isMetaLandingPage) return <MetaLandingPage />;

  return (
    <div className="min-h-screen bg-white text-slate-900 flex flex-col">
      <Header
        isScrolled={isScrolled}
        isMenuOpen={isMenuOpen}
        setIsMenuOpen={setIsMenuOpen}
        currentPage={currentPage}
      />
      <main className="pt-16 flex-1">
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/about" element={<AboutUs />} />
          <Route path="/terms" element={<TermsOfService />} />
          <Route path="/features" element={<FeaturesGPT />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogPage />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
          <Route path="/ask" element={<AskPage />} />
          <Route path="/results" element={<ResultsPage />} />
          <Route path="/best-demat-account-india" element={<DematPage />} />
          <Route path="/nifty-ai-analysis" element={<IndexAnalysisPage config={NIFTY_CONFIG} />} />
          <Route path="/bank-nifty-ai-analysis" element={<IndexAnalysisPage config={BANK_NIFTY_CONFIG} />} />
          <Route path="/adminxyz" element={<AdminPanel />} />
          <Route path="/coursesxyz" element={<CoursesPage />} />
          <Route path="/mentorxyz" element={
            isMentorLoggedIn
              ? <MentorDashboard onLogout={() => setIsMentorLoggedIn(false)} />
              : <MentorAuth onLogin={() => setIsMentorLoggedIn(true)} />
          } />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </main>

      <ExitIntentPopup />
      <MarketTicker />

      {/* ── Footer ───────────────────────────────────────────────────────── */}
      <footer className="bg-navy-950 border-t border-slate-800">
        {/* Main footer grid */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10">

            {/* Brand col */}
            <div className="md:col-span-5">
              <Link to="/" className="flex items-center mb-4 hover:opacity-80 transition-opacity w-fit">
                <span className="font-sans font-bold text-white text-base">ISMGPT <span className="text-slate-400 font-normal text-sm">Research</span></span>
              </Link>
              <p className="text-sm text-slate-400 leading-relaxed max-w-xs mb-5">
                Free AI-powered educational research for Indian retail investors.
                Analysis for any NSE or BSE stock in under 90 seconds.
              </p>
              {/* Community links */}
              <div className="flex gap-3">
                <a
                  href="https://tinyurl.com/stockmarketgpt"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-xs text-slate-400 hover:text-white transition-colors border border-slate-700 px-3 py-1.5 rounded-md hover:border-slate-500"
                >
                  Telegram
                </a>
              </div>
            </div>

            {/* Platform links */}
            <div className="md:col-span-2">
              <p className="text-xs text-slate-400 tracking-widest uppercase mb-4">Platform</p>
              <ul className="space-y-2.5">
                {[
                  { to: '/features', label: 'Features' },
                  { to: '/blog', label: 'Blog' },
                  { to: '/nifty-ai-analysis', label: 'Nifty 50 AI Analysis' },
                  { to: '/bank-nifty-ai-analysis', label: 'Bank Nifty AI Analysis' },
                ].map(({ to, label }) => (
                  <li key={to}>
                    <Link to={to} className="text-sm text-slate-300 hover:text-white transition-colors">
                      {label}
                    </Link>
                  </li>
                ))}
                <li>
                  <a
                    href="https://chatgpt.com/g/g-jkxrUFNja-indian-stock-market"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sm text-slate-300 hover:text-white transition-colors"
                  >
                    GPT Tool ↗
                  </a>
                </li>
              </ul>
            </div>

            {/* Company links */}
            <div className="md:col-span-2">
              <p className="text-xs text-slate-400 tracking-widest uppercase mb-4">Company</p>
              <ul className="space-y-2.5">
                {[
                  { to: '/about', label: 'About' },
                ].map(({ to, label }) => (
                  <li key={to}>
                    <Link to={to} className="text-sm text-slate-300 hover:text-white transition-colors">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Legal links */}
            <div className="md:col-span-3">
              <p className="text-xs text-slate-400 tracking-widest uppercase mb-4">Legal</p>
              <ul className="space-y-2.5">
                {[
                  { to: '/terms', label: 'Terms of Service' },
                  { to: '/privacy', label: 'Privacy Policy' },
                ].map(({ to, label }) => (
                  <li key={to}>
                    <Link to={to} className="text-sm text-slate-300 hover:text-white transition-colors">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

          </div>
        </div>

        {/* Bottom bar */}
        <div className="border-t border-slate-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
            <p className="text-xs text-slate-400">
              © {new Date().getFullYear()} IndianStockMarketGPT. All rights reserved.
            </p>
            <p className="text-xs text-slate-400 max-w-sm text-right">
              Not a SEBI-registered adviser. All content is for educational purposes only and does not constitute investment advice.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

// Route tree shared by the browser app and the build-time prerenderer
// (src/entry-server.tsx renders it inside a StaticRouter).
export function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <GptClickTracker />
      <Routes>
        <Route path="/ad" element={<MetaLandingPage />} />
        <Route path="/call" element={<CallPage />} />
        <Route path="/*" element={<AppContent />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
}

export default App;
